import React from 'react';
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, HTMLAttributes, CSSProperties, ReactNode, ComponentType } from 'react';

import PropTypes from 'prop-types';

import styled, { css, DefaultTheme } from 'styled-components';

import fonts from 'apps/promo/theme/fonts';
import media from 'apps/promo/theme/media';

export type TextColor =
    | 'darkGray'
    | 'mediumGray'
    | 'lightGray'
    | 'superLightGray'
    | 'purple'
    | 'purpleRgba40'
    | 'purpleRgba24'
    | 'red'
    | 'white'
    | 'white_base'
    | 'whiteRgba40'
    | 'whiteRgba64'
    | 'whiteRgba72';

export type TextSize = 'display1' | 'display2' | 'header' | 'main' | 'secondary' | 'subheader' | 'tag' | 'note';

export type TextVariant = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'pre' | 'paragraph' | 'div' | 'span' | 'link' | 'button' | 'listItem';

export type TextIndent =
    | '4px'
    | '8px'
    | '12px'
    | '16px'
    | '24px'
    | '32px'
    | '48px'
    | '64px'
    | 'adaptive-8-16'
    | 'adaptive-16-24'
    | 'adaptive-24-32'
    | 'adaptive-32-48';

interface CommonTextStyleProps {
    color?: TextColor;
    size?: TextSize;
    smallCaps?: boolean;
    nowrap?: boolean;
    leftSpacer?: boolean;
    ellipsis?: boolean;
    indent?: TextIndent;
    lineThrough?: boolean;
    textAlign?: CSSProperties['textAlign'];
    flexShrink?: number | string;
    isBold?: boolean;
}

type NativeTextElementProps = Omit<HTMLAttributes<HTMLElement> & AnchorHTMLAttributes<HTMLAnchorElement> & ButtonHTMLAttributes<HTMLButtonElement>, 'color'>;

export interface TextProps extends NativeTextElementProps, CommonTextStyleProps {
    variant?: TextVariant;
    children?: ReactNode;
    dataTestId?: string;
}

type TextTheme = DefaultTheme & {
    black: string;
    grey_72_lc: string;
    grey_40_lc: string;
    super_light_base: string;
    purple_100: string;
    purple_40_tr: string;
    purple_24_tr: string;
    pink_100: string;
    white: string;
    white_base: string;
    white_40_tr_base: string;
    white_64_tr_base: string;
    white_72_tr_base: string;
};

const getTheme = (props: { theme: DefaultTheme }): TextTheme => props.theme as TextTheme;
const shouldForwardTextProp = (prop: string) =>
    !['color', 'size', 'smallCaps', 'nowrap', 'leftSpacer', 'ellipsis', 'indent', 'lineThrough', 'textAlign', 'flexShrink', 'isBold'].includes(prop);

const getTextColor = (props: CommonTextStyleProps & { theme: DefaultTheme }) => {
    const theme = getTheme(props);
    switch (props.color) {
        case 'darkGray':
            return theme.black;
        case 'mediumGray':
            return theme.grey_72_lc;
        case 'lightGray':
            return theme.grey_40_lc;
        case 'superLightGray':
            return theme.super_light_base;
        case 'purple':
            return theme.purple_100;
        case 'purpleRgba40':
            return theme.purple_40_tr;
        case 'purpleRgba24':
            return theme.purple_24_tr;
        case 'red':
            return theme.pink_100;
        case 'white':
            return theme.white;
        case 'white_base':
            return theme.white_base;
        case 'whiteRgba40':
            return theme.white_40_tr_base;
        case 'whiteRgba64':
            return theme.white_64_tr_base;
        case 'whiteRgba72':
            return theme.white_72_tr_base;
        default:
            return theme.black;
    }
};

const getMobileSize = (props: CommonTextStyleProps) => {
    switch (props.size) {
        case 'display1':
            return '32px';
        case 'display2':
            return '40px';
        case 'header':
            return '24px';
        case 'main':
            return '17px';
        case 'secondary':
            return '14px';
        case 'subheader':
            return '14px';
        case 'tag':
        case 'note':
            return '8px';
        default:
            return '17px';
    }
};
const getMobileLineHeight = (props: CommonTextStyleProps) => {
    switch (props.size) {
        case 'display2':
            return '48px';
        case 'display1':
            return '40px';
        case 'header':
            return '36px';
        case 'main':
            return '24px';
        case 'secondary':
            return '20px';
        case 'subheader':
            return '20px';
        case 'tag':
        case 'note':
            return '10px';
        default:
            return '24px';
    }
};
const getDesktopSize = (props: CommonTextStyleProps) => {
    switch (props.size) {
        case 'display1':
            return '40px';
        case 'display2':
            return '48px';
        case 'header':
            return '32px';
        case 'main':
            return '20px';
        case 'secondary':
            return '17px';
        case 'subheader':
            return '17px';
        case 'tag':
        case 'note':
            return '11px';
        default:
            return '20px';
    }
};
const getDesktopLineHeight = (props: CommonTextStyleProps) => {
    switch (props.size) {
        case 'display1':
            return '56px';
        case 'display2':
            return '64px';
        case 'header':
            return '48px';
        case 'main':
            return '32px';
        case 'secondary':
            return '24px';
        case 'subheader':
            return '24px';
        case 'tag':
        case 'note':
            return '16px';
        default:
            return '32px';
    }
};

const getBottomIndent = (props: CommonTextStyleProps & { indent?: TextIndent }) => {
    switch (props.indent) {
        case '4px':
            return '4px';
        case '8px':
            return '8px';
        case '12px':
            return '12px';
        case '16px':
            return '16px';
        case '24px':
            return '24px';
        case '32px':
            return '32px';
        case '48px':
            return '48px';
        case '64px':
            return '64px';
        case 'adaptive-8-16':
            return '8px';
        case 'adaptive-16-24':
            return '16px';
        case 'adaptive-24-32':
            return '24px';
        case 'adaptive-32-48':
            return '32px';
        default:
            return null;
    }
};
const getDesktopBottomIndent = (props: CommonTextStyleProps & { indent?: TextIndent }) => {
    switch (props.indent) {
        case '4px':
            return '4px';
        case '8px':
            return '8px';
        case '12px':
            return '12px';
        case '16px':
            return '16px';
        case '24px':
            return '24px';
        case '32px':
            return '32px';
        case '48px':
            return '48px';
        case '64px':
            return '64px';
        case 'adaptive-8-16':
            return '16px';
        case 'adaptive-16-24':
            return '24px';
        case 'adaptive-24-32':
            return '32px';
        case 'adaptive-32-48':
            return '48px';
        default:
            return null;
    }
};

const CommonCss = css<CommonTextStyleProps>`
    font-family: ${fonts.gtStandardMMedium};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    font-size: ${(props) => getMobileSize(props)};
    line-height: ${(props) => getMobileLineHeight(props)};
    font-variant: ${(props) => (props.smallCaps ? 'small-caps' : null)};
    letter-spacing: ${(props) => (props.size === 'subheader' || props.size === 'tag' ? '0.08em' : null)};
    text-transform: ${(props) => (props.size === 'subheader' || props.size === 'tag' ? 'uppercase' : null)};
    color: ${(props) => getTextColor(props)};
    font-weight: ${(props) => (props.size === 'tag' || props.isBold ? 'bold' : null)};
    margin-left: ${(props) => (props.leftSpacer ? '3px' : null)};
    margin-bottom: ${(props) => getBottomIndent(props)};
    white-space: ${(props) => (props.nowrap || props.ellipsis ? 'nowrap' : null)};
    overflow: ${(props) => (props.ellipsis ? 'hidden' : null)};
    text-overflow: ${(props) => (props.ellipsis ? 'ellipsis' : null)};
    max-width: ${(props) => (props.ellipsis ? '100%' : null)};
    text-decoration: ${(props) => (props.lineThrough ? 'line-through' : null)};
    text-align: ${(props) => (props.textAlign ? props.textAlign : null)};
    flex-shrink: ${(props) => (props.flexShrink ? props.flexShrink : null)};

    &:link,
    &:visited {
        color: ${(props) => getTextColor(props)};
    }

    ${media.desktop1920} {
        font-size: ${(props) => getDesktopSize(props)};
        line-height: ${(props) => getDesktopLineHeight(props)};
        margin-bottom: ${(props) => getDesktopBottomIndent(props)};
    }
`;

const TextSpan = styled.span.withConfig({ shouldForwardProp: shouldForwardTextProp })<CommonTextStyleProps>`
    ${CommonCss};
`;
const TextDiv = styled.div.withConfig({ shouldForwardProp: shouldForwardTextProp })<CommonTextStyleProps>`
    ${CommonCss};
`;
const TextHeader1 = styled.h1.withConfig({ shouldForwardProp: shouldForwardTextProp })<CommonTextStyleProps>`
    ${CommonCss};
`;
const TextHeader2 = styled.h2.withConfig({ shouldForwardProp: shouldForwardTextProp })<CommonTextStyleProps>`
    ${CommonCss};
`;
const TextHeader3 = styled.h3.withConfig({ shouldForwardProp: shouldForwardTextProp })<CommonTextStyleProps>`
    ${CommonCss};
`;
const TextHeader4 = styled.h4.withConfig({ shouldForwardProp: shouldForwardTextProp })<CommonTextStyleProps>`
    ${CommonCss};
`;
const TextHeader5 = styled.h5.withConfig({ shouldForwardProp: shouldForwardTextProp })<CommonTextStyleProps>`
    ${CommonCss};
`;
const TextHeader6 = styled.h6.withConfig({ shouldForwardProp: shouldForwardTextProp })<CommonTextStyleProps>`
    ${CommonCss};
`;
const TextPre = styled.pre.withConfig({ shouldForwardProp: shouldForwardTextProp })<CommonTextStyleProps>`
    ${CommonCss};
`;
const TextParagraph = styled.p.withConfig({ shouldForwardProp: shouldForwardTextProp })<CommonTextStyleProps>`
    ${CommonCss};
`;
const TextLink = styled.a.withConfig({ shouldForwardProp: shouldForwardTextProp })<CommonTextStyleProps>`
    ${CommonCss};
`;
const TextButton = styled.button.withConfig({ shouldForwardProp: shouldForwardTextProp })<CommonTextStyleProps>`
    ${CommonCss};
    width: auto;
    cursor: pointer;
    background: none;
    border: none;
    padding: 0;
    text-align: left;

    &:hover,
    &:focus {
        background: none;
    }
`;
const TextListItem = styled.li.withConfig({ shouldForwardProp: shouldForwardTextProp })<CommonTextStyleProps>`
    ${CommonCss};
`;

const variantComponentMap: Record<TextVariant, ComponentType<any>> = {
    h1: TextHeader1,
    h2: TextHeader2,
    h3: TextHeader3,
    h4: TextHeader4,
    h5: TextHeader5,
    h6: TextHeader6,
    pre: TextPre,
    paragraph: TextParagraph,
    div: TextDiv,
    span: TextSpan,
    link: TextLink,
    button: TextButton,
    listItem: TextListItem,
};

const getComponent = (variant?: TextVariant) => {
    if (!variant) {
        return TextSpan;
    }
    return variantComponentMap[variant] || TextSpan;
};

export const Text = ({ variant = 'span', dataTestId, children, ...rest }: TextProps) => {
    const Wrapper = getComponent(variant);
    return (
        <Wrapper {...rest} data-testid={dataTestId}>
            {children}
        </Wrapper>
    );
};

Text.propTypes = {
    variant: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'pre', 'paragraph', 'div', 'span', 'link', 'button', 'listItem']),
    color: PropTypes.oneOf([
        'darkGray',
        'mediumGray',
        'lightGray',
        'superLightGray',
        'purple',
        'purpleRgba40',
        'purpleRgba24',
        'red',
        'white',
        'white_base',
        'whiteRgba40',
        'whiteRgba64',
        'whiteRgba72',
    ]),
    size: PropTypes.oneOf(['display1', 'display2', 'header', 'main', 'secondary', 'subheader', 'tag', 'note']),
    smallCaps: PropTypes.bool,
    nowrap: PropTypes.bool,
    leftSpacer: PropTypes.bool,
    ellipsis: PropTypes.bool,
    href: PropTypes.string,
    onClick: PropTypes.func,
    indent: PropTypes.oneOf([
        '4px',
        '8px',
        '12px',
        '16px',
        '24px',
        '32px',
        '48px',
        '64px',
        'adaptive-8-16',
        'adaptive-16-24',
        'adaptive-24-32',
        'adaptive-32-48',
    ]),
    lineThrough: PropTypes.bool,
    textAlign: PropTypes.string,
    flexShrink: PropTypes.string,
    isBold: PropTypes.bool,
    type: PropTypes.oneOf(['button', 'submit']),
};
